import React, { useEffect, useState, useMemo, useRef } from "react";
import { FaEdit, FaSave, FaTimes, FaSearch, FaMapMarkerAlt, FaMapSigns, FaCheckCircle, FaExclamationTriangle, FaArrowLeft } from "react-icons/fa";
import { AxiosPostMethod, AxiosPutMethod } from "../helpers/customAxios";
import { ErrorToast, SuccessToast } from "../components/toastify";
import { useDispatch } from "react-redux";
import { setShowPreloader } from "../store/activePage";
import { Formik, Form, useField, useFormikContext } from "formik";
import { useLocation, useNavigate } from "react-router-dom";

// Custom TargetVillageSelect component
const TargetVillageSelect = ({ name, availableVillages, ...props }) => {
  const [field, meta, helpers] = useField(name);
  const { setFieldValue } = useFormikContext();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredVillages = useMemo(() => {
    if (!search) return availableVillages;
    const lowerQuery = search.toLowerCase();
    return availableVillages.filter(v => v.toLowerCase().includes(lowerQuery));
  }, [availableVillages, search]);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <input
        {...field}
        {...props}
        type="text"
        placeholder="Select or type a target village"
        onClick={() => setIsOpen(true)}
        onChange={(e) => {
          field.onChange(e);
          setSearch(e.target.value);
          setIsOpen(true);
        }}
        value={isOpen ? search : field.value}
        className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all shadow-sm"
      />
      {isOpen && (
        <div className="absolute z-[9999] w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
          {filteredVillages.length > 0 && (
            filteredVillages.map((village, idx) => (
              <div
                key={idx}
                className="px-4 py-2 hover:bg-indigo-50 cursor-pointer text-sm text-slate-700 transition-colors"
                onClick={() => {
                  setFieldValue(name, village);
                  setSearch("");
                  setIsOpen(false);
                }}
              >
                {village}
              </div>
            ))
          )}
          
          {search && !availableVillages.some(v => v.toLowerCase() === search.toLowerCase()) ? (
            <div
              className={`px-4 py-2 hover:bg-indigo-50 cursor-pointer text-sm text-indigo-600 font-medium transition-colors ${filteredVillages.length > 0 ? 'border-t border-slate-100' : ''}`}
              onClick={() => {
                setFieldValue(name, search);
                setSearch("");
                setIsOpen(false);
              }}
            >
              + Add "{search}" as new village
            </div>
          ) : (
            filteredVillages.length === 0 && (
              <div className="px-4 py-2 text-sm text-slate-500 italic">No matching villages found.</div>
            )
          )}
        </div>
      )}
    </div>
  );
};

const VillageManager = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const eventId = location.state?.event_id;

  const [entries, setEntries] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [mergeSource, setMergeSource] = useState(null); // the village name(s) to be replaced (string or array)
  const [selectedVillages, setSelectedVillages] = useState([]);

  // Collapse visually identical Unicode strings while retaining their raw
  // values for merge requests, which must match the stored database strings.
  const normalizeVillageName = (value) =>
    String(value || "")
      .normalize("NFC")
      .replace(/[\u00AD\u200B-\u200D\u2060\uFEFF]/g, "")
      .replace(/\s+/gu, " ")
      .trim();

  const getVillageKey = (value) => normalizeVillageName(value).toLocaleLowerCase();

  // Fetch all entries
  const fetchEntries = async () => {
    if (!eventId) return;
    dispatch(setShowPreloader(true));
    try {
      const res = await AxiosPostMethod("org/get-token-info", { event_id: eventId });
      if (res && res.data && res.data.data) {
        setEntries(res.data.data);
      }
    } catch (error) {
      console.error(error);
      ErrorToast("Failed to fetch entries");
    } finally {
      dispatch(setShowPreloader(false));
    }
  };

  useEffect(() => {
    if (!eventId) {
      navigate("/dashboard/upcoming-events");
      return;
    }
    fetchEntries();
  }, [eventId]);

  const getGroupKey = (word) => {
    if (!word) return "";
    return getVillageKey(word).replace(/[\s\d.,'-]+/g, '').slice(0, 4);
  };

  // Compute village statistics
  const { groupedVillagesData, flatVillagesData } = useMemo(() => {
    const map = new Map();
    entries.forEach((entry) => {
      const originalName = entry.village ? String(entry.village).trim() : "";
      const displayName = normalizeVillageName(originalName);
      const key = getVillageKey(originalName);
      if (displayName && key) {
        const current = map.get(key) || { key, name: displayName, count: 0, sourceNames: [] };
        current.count += 1;
        if (!current.sourceNames.includes(originalName)) current.sourceNames.push(originalName);
        map.set(key, current);
      }
    });

    let list = Array.from(map.values());

    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      list = list.filter((v) => v.name.toLowerCase().includes(lowerQuery));
    }

    // Sort alphabetically so the section title is deterministic
    list.sort((a, b) => a.name.localeCompare(b.name));

    // Grouping
    const groups = {};
    list.forEach(v => {
      const key = getGroupKey(v.name);
      if (!groups[key]) {
        groups[key] = {
          title: v.name,
          items: []
        };
      }
      groups[key].items.push(v);
    });

    const groupedList = Object.values(groups);
    // Sort groups alphabetically by title
    groupedList.sort((a, b) => a.title.localeCompare(b.title));
    
    // Sort items inside each group by count (descending)
    groupedList.forEach(g => {
      g.items.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    });

    // We also return a flat list sorted descending by count for the dropdown
    const flatList = Array.from(map.values());
    flatList.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

    return { groupedVillagesData: groupedList, flatVillagesData: flatList };
  }, [entries, searchQuery]);

  const availableTargetVillages = useMemo(() => {
    return flatVillagesData
      .filter(v => !(Array.isArray(mergeSource) ? mergeSource : [mergeSource]).some(
        source => getVillageKey(source) === v.key
      ))
      .map(v => v.name);
  }, [flatVillagesData, mergeSource]);

  const handleMergeSubmit = async (values, actions) => {
    const targetVillage = values.targetVillage?.trim();
    
    if (!targetVillage) {
      ErrorToast("Target village is required.");
      actions.setSubmitting(false);
      return;
    }
    
    const sourceVillages = Array.isArray(mergeSource) ? mergeSource : [mergeSource];
    if (sourceVillages.some(source => getVillageKey(source) === getVillageKey(targetVillage))) {
      ErrorToast("Source and target village cannot be the same.");
      actions.setSubmitting(false);
      return;
    }

    dispatch(setShowPreloader(true));
    try {
      await AxiosPutMethod("org/village/merge", {
        event_id: eventId,
        source_village: sourceVillages,
        target_village: targetVillage,
      });
      SuccessToast("Villages merged successfully!");
      setMergeSource(null);
      setSelectedVillages([]);
      fetchEntries(); // Refresh entries to recalculate counts
    } catch (error) {
      console.error("Merge error:", error);
      ErrorToast(error?.response?.data?.message || "Failed to merge villages.");
    } finally {
      dispatch(setShowPreloader(false));
      actions.setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
          <div>
            <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-slate-600 mb-2 flex items-center gap-1 text-sm font-semibold transition">
              <FaArrowLeft /> Back
            </button>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <FaMapSigns className="text-indigo-600" />
              Manage Villages
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              Consolidate duplicate or misspelled village names for this event.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full sm:w-auto">
            {selectedVillages.length > 0 && (
              <button
                onClick={() => setMergeSource(flatVillagesData
                  .filter(village => selectedVillages.includes(village.key))
                  .flatMap(village => village.sourceNames))}
                className="inline-flex justify-center items-center gap-2 px-4 py-2.5 text-sm font-bold text-white bg-amber-600 rounded-xl hover:bg-amber-700 transition-all shadow-sm whitespace-nowrap"
              >
                <FaEdit />
                Merge {selectedVillages.length} Selected
              </button>
            )}
            <div className="relative w-full sm:w-72">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search villages..."
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Villages List */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            <div className="col-span-7 sm:col-span-8 flex items-center gap-3">
              <input
                type="checkbox"
                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                checked={selectedVillages.length === flatVillagesData.length && flatVillagesData.length > 0}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedVillages(flatVillagesData.map(v => v.key));
                  } else {
                    setSelectedVillages([]);
                  }
                }}
              />
              Village Name
            </div>
            <div className="col-span-2 text-center">Entries</div>
            <div className="col-span-3 sm:col-span-2 text-right">Action</div>
          </div>
          
          <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto relative">
            {groupedVillagesData.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                No villages found.
              </div>
            ) : (
              groupedVillagesData.map((group, gIdx) => (
                <div key={gIdx}>
                  <div className="sticky top-0 z-10 bg-slate-100/95 backdrop-blur-sm px-6 py-2 text-sm font-bold text-slate-700 border-y border-slate-200 shadow-sm flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span>{group.title}</span> 
                      <span className="text-slate-400 font-normal text-xs">({group.items.length} {group.items.length === 1 ? 'variant' : 'variants'})</span>
                    </div>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {group.items.map((village, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-slate-50/50 transition-colors bg-white">
                        <div className="col-span-7 sm:col-span-8 font-medium text-slate-700 truncate pr-4 pl-4 flex items-center gap-3">
                          <input
                            type="checkbox"
                            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4 flex-shrink-0"
                            checked={selectedVillages.includes(village.key)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedVillages([...selectedVillages, village.key]);
                              } else {
                                setSelectedVillages(selectedVillages.filter(v => v !== village.key));
                              }
                            }}
                          />
                          <span className="truncate">{village.name}</span>
                        </div>
                        <div className="col-span-2 text-center">
                          <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 font-semibold text-xs border border-indigo-100">
                            {village.count}
                          </span>
                        </div>
                        <div className="col-span-3 sm:col-span-2 text-right">
                          <button
                            onClick={() => setMergeSource(village.sourceNames)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1"
                          >
                            <FaEdit className="text-amber-500" />
                            Merge
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

      </div>

      {/* Merge Modal */}
      {mergeSource && (
        <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full animate-[scaleUp_0.2s_ease-out]">
            <style>{`
              @keyframes scaleUp {
                from { opacity: 0; transform: scale(0.95); }
                to { opacity: 1; transform: scale(1); }
              }
            `}</style>
            
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50 rounded-t-2xl">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <FaExclamationTriangle className="text-amber-500" />
                Merge Village
              </h3>
              <button 
                onClick={() => setMergeSource(null)}
                className="text-slate-400 hover:text-slate-600 transition-colors p-1"
              >
                <FaTimes size={18} />
              </button>
            </div>

            <Formik
              initialValues={{ targetVillage: "" }}
              onSubmit={handleMergeSubmit}
            >
              {({ isSubmitting }) => (
                <Form className="p-6 space-y-6">
                  
                  <div className="space-y-4">
                    <div className="bg-amber-50 text-amber-800 p-4 rounded-xl text-sm border border-amber-200 leading-relaxed">
                      All entries currently assigned to <strong className="font-bold">{Array.isArray(mergeSource) ? `${mergeSource.length} selected villages` : `"${mergeSource}"`}</strong> will be moved to the new village you select below. This action cannot be easily undone.
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                        Target Village Name
                      </label>
                      <TargetVillageSelect 
                        name="targetVillage" 
                        availableVillages={availableTargetVillages}
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setMergeSource(null)}
                      className="flex-1 px-4 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 inline-flex justify-center items-center gap-2 px-4 py-2.5 text-sm font-bold text-white bg-indigo-600 border border-transparent rounded-xl hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all disabled:opacity-70"
                    >
                      {isSubmitting ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <FaCheckCircle />
                      )}
                      Save Changes
                    </button>
                  </div>
                </Form>
              )}
            </Formik>

          </div>
        </div>
      )}
    </div>
  );
};

export default VillageManager;
